import { z } from "zod";
import { AllergyCodeSystemSchema, ChartCodeSystemSchema, ChartSourceKindSchema, codeWithSystem, MedicationCodeSystemSchema, ProblemCategorySchema, ProblemClinicalStatusSchema, } from "./chart-history.js";
import { IdempotencyKeySchema, IdSchema, IsoDateTimeSchema } from "./primitives.js";
/**
 * Patient profile contracts: the durable patient record as served by
 * `api.get_patient_profile` and written by `api.update_patient_profile`
 * (replace-set per section, keyed by the `updatedAt` token) and
 * `api.update_patient_demographics` (keyed by the patient row version).
 * Migration 20260904020142.
 *
 * Sections: allergies, medications, pmh (past medical history), familyHistory
 * (narrative + structured rows), socialHistory, immunizations. Read shapes are
 * tolerant (unknown keys kept) so the server can grow them; write shapes are
 * strict mirrors of the RPC allow-lists so the agent-api fails fast with a 400.
 *
 * Role projection: front desk and clinic admin receive `demographics` only, so
 * every clinical section is optional on the read shape.
 */
const PositiveIntegerSchema = z.coerce.number().int().positive();
const IsoDateSchema = z.string().date();
export const PATIENT_PROFILE_SECTION_KEYS = [
    "allergies",
    "medications",
    "pmh",
    "familyHistory",
    "socialHistory",
    "immunizations",
];
export const PatientProfileSectionKeySchema = z.enum(PATIENT_PROFILE_SECTION_KEYS);
export const AllergySeveritySchema = z.enum(["mild", "moderate", "severe", "unknown"]);
export const AllergyStatusSchema = z.enum(["active", "inactive", "resolved"]);
export const MedicationStatusSchema = z.enum(["active", "inactive", "completed", "unknown"]);
export const FamilyRelationSchema = z.enum([
    "mother",
    "father",
    "sister",
    "brother",
    "daughter",
    "son",
    "maternal_grandmother",
    "maternal_grandfather",
    "paternal_grandmother",
    "paternal_grandfather",
    "aunt",
    "uncle",
    "cousin",
    "other",
]);
export const HistoryRowStatusSchema = z.enum(["active", "inactive"]);
export const SocialHistoryKindSchema = z.enum([
    "tobacco",
    "alcohol",
    "cannabis",
    "substance",
    "occupation",
    "housing",
    "exercise",
    "diet",
    "other",
]);
export const SocialHistoryStatusSchema = z.enum(["current", "former", "never", "unknown"]);
export const ImmunizationCodeSystemSchema = z.enum(["CVX", "SNOMED-CT", "local"]);
export const ImmunizationRouteSchema = z.enum(["IM", "SC", "ID", "oral", "intranasal", "other"]);
export const ImmunizationStatusSchema = z.enum(["completed", "entered_in_error", "not_done"]);
export const PatientAdministrativeSexSchema = z.enum(["female", "male", "unknown", "other"]);
export const CoveragePayerKindSchema = z.enum([
    "provincial_plan",
    "federal_program",
    "private_insurance",
    "other",
]);
export const CoverageRelationshipSchema = z.enum(["self", "spouse", "child", "other"]);
const readItemBase = {
    id: IdSchema,
    version: PositiveIntegerSchema,
    recordedAt: IsoDateTimeSchema,
    sourceKind: ChartSourceKindSchema.optional(),
};
const writeItemBase = {
    id: IdSchema.optional(),
    version: PositiveIntegerSchema.optional(),
    sourceKind: ChartSourceKindSchema.optional(),
};
function pairedCode(codeKey, systemKey) {
    return (value, context) => {
        if ((value[codeKey] === undefined) !== (value[systemKey] === undefined)) {
            context.addIssue({
                code: "custom",
                path: [value[codeKey] === undefined ? codeKey : systemKey],
                message: `${codeKey} and ${systemKey} must be provided together`,
            });
        }
    };
}
// ---------------------------------------------------------------------------
// Allergies
// ---------------------------------------------------------------------------
const allergyFields = {
    display: z.string().min(1).max(240),
    code: z.string().min(1).max(80).optional(),
    codeSystem: AllergyCodeSystemSchema.optional(),
    reaction: z.string().min(1).max(500).optional(),
    severity: AllergySeveritySchema.optional(),
};
export const AllergyItemSchema = z
    .object({ ...readItemBase, ...allergyFields, status: AllergyStatusSchema })
    .passthrough();
export const AllergyItemWriteSchema = z
    .object({ ...writeItemBase, ...allergyFields, status: AllergyStatusSchema.optional() })
    .strict()
    .superRefine(codeWithSystem("code", "codeSystem", "id"));
// ---------------------------------------------------------------------------
// Medications
// ---------------------------------------------------------------------------
const medicationFields = {
    display: z.string().min(1).max(240),
    code: z.string().min(1).max(80).optional(),
    codeSystem: MedicationCodeSystemSchema.optional(),
    sig: z.string().min(1).max(500).optional(),
};
export const MedicationItemSchema = z
    .object({ ...readItemBase, ...medicationFields, status: MedicationStatusSchema })
    .passthrough();
export const MedicationItemWriteSchema = z
    .object({ ...writeItemBase, ...medicationFields, status: MedicationStatusSchema.optional() })
    .strict()
    .superRefine(codeWithSystem("code", "codeSystem", "id"));
// ---------------------------------------------------------------------------
// Past medical history (clinical.conditions)
// ---------------------------------------------------------------------------
const pmhFields = {
    display: z.string().min(1).max(240),
    code: z.string().min(1).max(80).optional(),
    codeSystem: ChartCodeSystemSchema.optional(),
    onsetDate: IsoDateSchema.optional(),
    note: z.string().min(1).max(2000).optional(),
};
export const PmhItemSchema = z
    .object({
    ...readItemBase,
    ...pmhFields,
    category: ProblemCategorySchema,
    status: ProblemClinicalStatusSchema,
})
    .passthrough();
export const PmhItemWriteSchema = z
    .object({
    ...writeItemBase,
    ...pmhFields,
    category: ProblemCategorySchema.optional(),
    status: ProblemClinicalStatusSchema.optional(),
})
    .strict()
    .superRefine(pairedCode("code", "codeSystem"));
// ---------------------------------------------------------------------------
// Family history: narrative + structured rows
// ---------------------------------------------------------------------------
const familyHistoryRowFields = {
    relation: FamilyRelationSchema,
    display: z.string().min(1).max(240),
    code: z.string().min(1).max(80).optional(),
    codeSystem: ChartCodeSystemSchema.optional(),
    onsetAge: z.coerce.number().int().min(0).max(130).optional(),
    deceased: z.boolean().optional(),
    note: z.string().min(1).max(2000).optional(),
};
export const FamilyHistoryRowSchema = z
    .object({ ...readItemBase, ...familyHistoryRowFields, status: HistoryRowStatusSchema })
    .passthrough();
export const FamilyHistoryRowWriteSchema = z
    .object({
    ...writeItemBase,
    ...familyHistoryRowFields,
    status: HistoryRowStatusSchema.optional(),
})
    .strict()
    .superRefine(pairedCode("code", "codeSystem"));
export const FamilyHistorySectionSchema = z
    .object({
    narrative: z.string().max(4000),
    narrativeRecordedAt: IsoDateTimeSchema.optional(),
    rows: z.array(FamilyHistoryRowSchema),
})
    .passthrough();
/** `narrative` absent = untouched, null or "" = cleared; `rows` absent = untouched, [] = cleared. */
export const FamilyHistorySectionWriteSchema = z
    .object({
    narrative: z.string().max(4000).nullable().optional(),
    rows: z.array(FamilyHistoryRowWriteSchema).max(200).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Social history (sparse: one row per captured kind)
// ---------------------------------------------------------------------------
const socialHistoryFields = {
    kind: SocialHistoryKindSchema,
    status: SocialHistoryStatusSchema,
    detail: z.string().min(1).max(500),
    quantity: z.string().min(1).max(120).optional(),
    frequency: z.string().min(1).max(120).optional(),
    startedOn: IsoDateSchema.optional(),
    endedOn: IsoDateSchema.optional(),
    note: z.string().min(1).max(2000).optional(),
};
export const SocialHistoryItemSchema = z
    .object({ ...readItemBase, ...socialHistoryFields, clinicalStatus: HistoryRowStatusSchema })
    .passthrough();
export const SocialHistoryItemWriteSchema = z
    .object({
    ...writeItemBase,
    ...socialHistoryFields,
    clinicalStatus: HistoryRowStatusSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Immunizations
// ---------------------------------------------------------------------------
const immunizationFields = {
    display: z.string().min(1).max(240),
    code: z.string().min(1).max(20).optional(),
    codeSystem: ImmunizationCodeSystemSchema.optional(),
    administeredOn: IsoDateSchema,
    lotNumber: z.string().min(1).max(80).optional(),
    site: z.string().min(1).max(80).optional(),
    route: ImmunizationRouteSchema.optional(),
    doseNumber: z.coerce.number().int().min(1).max(20).optional(),
    note: z.string().min(1).max(2000).optional(),
};
export const ImmunizationItemSchema = z
    .object({ ...readItemBase, ...immunizationFields, status: ImmunizationStatusSchema })
    .passthrough();
export const ImmunizationItemWriteSchema = z
    .object({ ...writeItemBase, ...immunizationFields, status: ImmunizationStatusSchema.optional() })
    .strict()
    .superRefine(pairedCode("code", "codeSystem"));
// ---------------------------------------------------------------------------
// Demographics and coverage
// ---------------------------------------------------------------------------
export const PatientAddressSchema = z
    .object({
    line1: z.string().max(200).optional(),
    line2: z.string().max(200).optional(),
    city: z.string().max(120).optional(),
    region: z.string().max(120).optional(),
    province: z.string().max(120).optional(),
    state: z.string().max(120).optional(),
    postalCode: z.string().max(20).optional(),
    country: z.string().max(120).optional(),
})
    .passthrough();
export const PatientEmergencyContactSchema = z
    .object({
    name: z.string().max(200).optional(),
    relationship: z.string().max(80).optional(),
    phone: z.string().max(40).optional(),
})
    .passthrough();
/** The coverage every document prints (current plans first, provincial before private). */
export const PatientCoverageSchema = z
    .object({
    id: IdSchema,
    payerName: z.string().min(1),
    payerKind: CoveragePayerKindSchema.optional(),
    memberId: z.string().min(1),
    groupNumber: z.string().optional(),
    relationship: z.string().optional(),
    status: z.enum(["active", "inactive", "pending_verification"]),
    eligibilityStatus: z.enum(["active", "inactive", "unknown"]).optional(),
    lastVerifiedAt: IsoDateTimeSchema.optional(),
    /**
     * The version code of the Ontario health card whose number is the printed
     * health number (clinical.patient_external_identifiers, system
     * ca-on:health-card), as billing and forms read it. Migration
     * 20260928132206 onward; absent when none is recorded.
     */
    versionCode: z.string().optional(),
    /**
     * The expiry date printed on that card, as recorded: `YYYY-MM-DD` when
     * typed at the front desk, or as a chart import wrote it. Migration
     * 20261007022422 onward; absent when none is recorded.
     */
    expiryDate: z.string().optional(),
})
    .passthrough();
const insuranceCoverageInputFields = {
    kind: z.literal("insurance"),
    payerName: z.string().min(2).max(160),
    payerKind: CoveragePayerKindSchema.optional(),
    memberId: z.string().min(2).max(120),
    groupNumber: z.string().min(1).max(80).optional(),
    relationship: CoverageRelationshipSchema.optional(),
};
/** Same shape api.create_walk_in accepts; a change inactivates the current plans. */
export const PatientCoverageInputSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("cash") }).strict(),
    z.object(insuranceCoverageInputFields).strict(),
]);
/**
 * The version code printed beside an Ontario health number: two letters, or
 * one on some older cards. Typed in any case; an empty string clears it.
 */
export const HealthCardVersionCodeInputSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{0,2}$/, "Use one or two letters, like AB");
/**
 * The expiry date printed on an Ontario health card, `YYYY-MM-DD` (a real
 * calendar date); an empty string clears it.
 */
export const HealthCardExpiryDateInputSchema = z
    .string()
    .trim()
    .refine((value) => value === "" || IsoDateSchema.safeParse(value).success, "Use a date like 2028-03-31");
/**
 * The coverage api.update_patient_demographics, api.register_patient and
 * api.create_walk_in accept: the walk-in shape plus the health card version
 * code and expiry date, which only a provincial plan carries. An absent
 * versionCode or expiryDate leaves the recorded one alone (a changed health
 * number takes it along); an empty one clears it.
 */
export const PatientCoverageUpdateInputSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("cash") }).strict(),
    z
        .object({
        ...insuranceCoverageInputFields,
        versionCode: HealthCardVersionCodeInputSchema.optional(),
        expiryDate: HealthCardExpiryDateInputSchema.optional(),
    })
        .strict()
        .superRefine((value, context) => {
        if (value.versionCode !== undefined && value.payerKind !== "provincial_plan") {
            context.addIssue({
                code: "custom",
                path: ["versionCode"],
                message: "Only a provincial health card has a version code",
            });
        }
        if (value.expiryDate !== undefined && value.payerKind !== "provincial_plan") {
            context.addIssue({
                code: "custom",
                path: ["expiryDate"],
                message: "Only a provincial health card has an expiry date",
            });
        }
    }),
]);
export const PatientDemographicsSchema = z
    .object({
    mrn: z.string().min(1),
    legalFirstName: z.string().min(1),
    /** Optional legal middle name(s); absent when none is recorded (20261001202120). */
    legalMiddleName: z.string().optional(),
    legalLastName: z.string().min(1),
    preferredName: z.string().optional(),
    dateOfBirth: IsoDateSchema,
    administrativeSex: PatientAdministrativeSexSchema,
    phone: z.string().optional(),
    email: z.string().optional(),
    address: PatientAddressSchema.optional(),
    emergencyContact: PatientEmergencyContactSchema.optional(),
    healthNumber: z.string().optional(),
    coverage: PatientCoverageSchema.optional(),
    status: z.string().min(1),
    version: PositiveIntegerSchema,
})
    .passthrough();
/**
 * Mirrors the p_demographics allow-list of api.update_patient_demographics.
 * `expectedVersion` travels inside the payload (it is part of the request hash);
 * an empty string clears an optional text field; null clears address or contact.
 */
export const PatientDemographicsUpdateInputSchema = z
    .object({
    expectedVersion: PositiveIntegerSchema,
    legalFirstName: z.string().min(1).max(100).optional(),
    legalMiddleName: z.string().max(100).optional(),
    legalLastName: z.string().min(1).max(100).optional(),
    preferredName: z.string().max(120).optional(),
    dateOfBirth: IsoDateSchema.optional(),
    administrativeSex: PatientAdministrativeSexSchema.optional(),
    phone: z.string().max(40).optional(),
    email: z.string().max(320).optional(),
    address: PatientAddressSchema.nullable().optional(),
    emergencyContact: PatientEmergencyContactSchema.nullable().optional(),
    coverage: PatientCoverageUpdateInputSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Profile read shape, update input, receipts, HTTP request bodies
// ---------------------------------------------------------------------------
/** A recent coded encounter diagnosis rolled up from signed visits (PR8 populates it). */
export const RecentEncounterDiagnosisSchema = z
    .object({
    codeSystem: z.string().min(1),
    code: z.string().min(1),
    display: z.string().min(1),
    lastSeenAt: IsoDateTimeSchema.optional(),
    encounterCount: z.coerce.number().int().nonnegative().optional(),
})
    .passthrough();
export const PatientProfileSchema = z
    .object({
    patientId: IdSchema,
    /** Millisecond-precision UTC token; echo it back verbatim on update. */
    updatedAt: IsoDateTimeSchema,
    demographics: PatientDemographicsSchema,
    allergies: z.array(AllergyItemSchema).optional(),
    medications: z.array(MedicationItemSchema).optional(),
    pmh: z.array(PmhItemSchema).optional(),
    familyHistory: FamilyHistorySectionSchema.optional(),
    socialHistory: z.array(SocialHistoryItemSchema).optional(),
    immunizations: z.array(ImmunizationItemSchema).optional(),
    recentEncounterDiagnoses: z.array(RecentEncounterDiagnosisSchema).optional(),
})
    .passthrough();
/** Mirrors the p_profile allow-list of api.update_patient_profile: any subset of sections. */
export const PatientProfileUpdateInputSchema = z
    .object({
    allergies: z.array(AllergyItemWriteSchema).max(200).optional(),
    medications: z.array(MedicationItemWriteSchema).max(200).optional(),
    pmh: z.array(PmhItemWriteSchema).max(200).optional(),
    familyHistory: FamilyHistorySectionWriteSchema.optional(),
    socialHistory: z.array(SocialHistoryItemWriteSchema).max(200).optional(),
    immunizations: z.array(ImmunizationItemWriteSchema).max(200).optional(),
})
    .strict();
export const PatientProfileSectionChangeCountsSchema = z
    .object({
    added: z.coerce.number().int().nonnegative(),
    updated: z.coerce.number().int().nonnegative(),
    removed: z.coerce.number().int().nonnegative(),
})
    .passthrough();
export const PatientProfileUpdateReceiptSchema = z
    .object({
    updated: z.literal(true),
    receiptId: IdSchema,
    summary: z.partialRecord(PatientProfileSectionKeySchema, PatientProfileSectionChangeCountsSchema),
    profile: PatientProfileSchema,
})
    .passthrough();
export const PatientDemographicsUpdateReceiptSchema = z
    .object({
    updated: z.literal(true),
    receiptId: IdSchema,
    changedFields: z.array(z.string().min(1)),
    profile: PatientProfileSchema,
})
    .passthrough();
/** `PUT /v1/patients/:patientId/profile` body. */
export const UpdatePatientProfileRequestSchema = z
    .object({
    siteId: IdSchema,
    idempotencyKey: IdempotencyKeySchema,
    expectedUpdatedAt: IsoDateTimeSchema,
    profile: PatientProfileUpdateInputSchema,
})
    .strict();
/** `PUT /v1/patients/:patientId/demographics` body. */
export const UpdatePatientDemographicsRequestSchema = z
    .object({
    siteId: IdSchema,
    idempotencyKey: IdempotencyKeySchema,
    demographics: PatientDemographicsUpdateInputSchema,
})
    .strict();
/**
 * One-line renderings of the current heads, matching the strings the patient
 * workspace RPC emits for the encounter context panels (so both surfaces read
 * the same record the same way). Inactive rows are omitted; resolved PMH stays.
 */
export function patientProfileToDisplayStrings(profile) {
    return {
        allergies: (profile.allergies ?? [])
            .filter((item) => item.status !== "inactive")
            .map((item) => (item.reaction ? `${item.display} — ${item.reaction}` : item.display)),
        medications: (profile.medications ?? [])
            .filter((item) => item.status === "active")
            .map((item) => (item.sig ? `${item.display} — ${item.sig}` : item.display)),
        pmh: (profile.pmh ?? [])
            .filter((item) => item.status !== "inactive")
            .map((item) => {
            const year = item.onsetDate?.slice(0, 4);
            const base = year ? `${item.display} — since ${year}` : item.display;
            return item.category === "surgical" ? `${base} (surgical)` : base;
        }),
        familyHistory: (profile.familyHistory?.rows ?? [])
            .filter((row) => row.status !== "inactive")
            .map((row) => `${row.relation.replace(/_/g, " ")}: ${row.display}`),
        socialHistory: (profile.socialHistory ?? [])
            .filter((item) => item.clinicalStatus !== "inactive")
            .map((item) => `${item.kind}: ${item.detail} (${item.status})`),
        immunizations: (profile.immunizations ?? [])
            .filter((item) => item.status === "completed")
            .map((item) => `${item.display} — ${item.administeredOn}`),
    };
}
//# sourceMappingURL=patient-profile.js.map